import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Award, TrendingUp, ClipboardCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = [
  { v: "test", label: "Test result", icon: TrendingUp },
  { v: "attendance", label: "Attendance", icon: ClipboardCheck },
  { v: "remark", label: "Teacher's remark", icon: MessageSquare },
  { v: "grade", label: "Subject grade", icon: Award },
];

const SUBJECTS = ["Mathematics", "Physics", "Chemistry", "Biology", "Science", "English", "Social Studies", "Hindi", "Computer Science"];

export default function AdminStudentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    entry_type: "test", subject: "", chapter: "", test_name: "",
    marks_obtained: "", max_marks: "", entry_date: "",
    attendance_percent: "", remark: "", grade_value: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/students/${id}`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addEntry = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    // Strip empties + coerce numbers
    ["marks_obtained", "max_marks", "attendance_percent"].forEach(k => {
      payload[k] = payload[k] === "" ? null : Number(payload[k]);
      if (Number.isNaN(payload[k])) payload[k] = null;
    });
    Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
    if (payload.entry_type === "test" && (!payload.subject || payload.marks_obtained == null || !payload.max_marks)) {
      toast.error("Subject, marks obtained and max marks are required for a test");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/students/${id}/progress`, payload);
      toast.success("Entry added");
      setForm({ entry_type: "test", subject: "", chapter: "", test_name: "", marks_obtained: "", max_marks: "", entry_date: "", attendance_percent: "", remark: "", grade_value: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (entryId) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/admin/progress/${entryId}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (!data) return <div className="text-slate-500">Student not found.</div>;

  const { student, progress } = data;
  const t = form.entry_type;

  return (
    <>
      <Link to="/admin/students" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B192C]"><ArrowLeft size={14} /> Back to students</Link>

      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-800 text-[#0B192C]">{student.name}</h1>
            <div className="text-sm text-slate-600 mt-1">Class {student.student_class} · {student.board} · Phone {student.phone}</div>
            {student.parent_name && <div className="text-sm text-slate-500">Parent: {student.parent_name}{student.parent_phone ? ` · ${student.parent_phone}` : ""}</div>}
          </div>
          <span className={`text-xs font-semibold px-2 py-1 rounded border ${student.active ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-500 bg-slate-100 border-slate-200"}`}>
            {student.active ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      {/* Add entry form */}
      <form onSubmit={addEntry} data-testid="add-entry-form" className="rounded-lg border border-slate-200 bg-white p-6 space-y-4">
        <h2 className="font-display text-xl font-bold text-[#0B192C]">Add progress entry</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {TYPES.map(({ v, label, icon: Icon }) => (
            <button key={v} type="button" onClick={() => setForm(f => ({ ...f, entry_type: v }))}
              data-testid={`type-${v}`}
              className={`p-3 rounded-md border text-sm font-medium flex items-center gap-2 justify-center transition-colors ${
                t === v ? "border-[#FF6B35] bg-[#FF6B35]/5 text-[#0B192C]" : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}>
              <Icon size={14} className={t === v ? "text-[#FF6B35]" : ""} /> {label}
            </button>
          ))}
        </div>

        {t === "test" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Subject *</Label>
              <Select value={form.subject} onValueChange={(v) => setForm(f => ({ ...f, subject: v }))}>
                <SelectTrigger data-testid="entry-subject" className="mt-1"><SelectValue placeholder="Select subject" /></SelectTrigger>
                <SelectContent>{SUBJECTS.map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Chapter / Topic</Label>
              <Input value={form.chapter} onChange={(e) => setForm(f => ({ ...f, chapter: e.target.value }))} data-testid="entry-chapter" className="mt-1" />
            </div>
            <div>
              <Label>Test name</Label>
              <Input value={form.test_name} onChange={(e) => setForm(f => ({ ...f, test_name: e.target.value }))} placeholder="e.g. Weekly Test 3" data-testid="entry-test-name" className="mt-1" />
            </div>
            <div>
              <Label>Marks obtained *</Label>
              <Input type="number" value={form.marks_obtained} onChange={(e) => setForm(f => ({ ...f, marks_obtained: e.target.value }))} data-testid="entry-marks" className="mt-1" />
            </div>
            <div>
              <Label>Max marks *</Label>
              <Input type="number" value={form.max_marks} onChange={(e) => setForm(f => ({ ...f, max_marks: e.target.value }))} data-testid="entry-max" className="mt-1" />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.entry_date} onChange={(e) => setForm(f => ({ ...f, entry_date: e.target.value }))} data-testid="entry-date" className="mt-1" />
            </div>
          </div>
        )}

        {t === "attendance" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Attendance %</Label>
              <Input type="number" min={0} max={100} value={form.attendance_percent} onChange={(e) => setForm(f => ({ ...f, attendance_percent: e.target.value }))} data-testid="entry-att" className="mt-1" />
            </div>
            <div>
              <Label>Period / Month</Label>
              <Input value={form.entry_date} onChange={(e) => setForm(f => ({ ...f, entry_date: e.target.value }))} placeholder="e.g. January 2026" data-testid="entry-att-date" className="mt-1" />
            </div>
            <div className="md:col-span-1">
              <Label>Note</Label>
              <Input value={form.remark} onChange={(e) => setForm(f => ({ ...f, remark: e.target.value }))} data-testid="entry-att-note" className="mt-1" />
            </div>
          </div>
        )}

        {t === "remark" && (
          <div>
            <Label>Teacher&apos;s remark</Label>
            <Textarea rows={3} value={form.remark} onChange={(e) => setForm(f => ({ ...f, remark: e.target.value }))} data-testid="entry-remark" className="mt-1" />
          </div>
        )}

        {t === "grade" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Subject</Label>
              <Select value={form.subject} onValueChange={(v) => setForm(f => ({ ...f, subject: v }))}>
                <SelectTrigger data-testid="entry-grade-subject" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{SUBJECTS.map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Grade</Label>
              <Select value={form.grade_value} onValueChange={(v) => setForm(f => ({ ...f, grade_value: v }))}>
                <SelectTrigger data-testid="entry-grade-value" className="mt-1"><SelectValue placeholder="A+/A/B+…" /></SelectTrigger>
                <SelectContent>
                  {["A+","A","B+","B","C+","C","D"].map(g => (<SelectItem key={g} value={g}>{g}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note</Label>
              <Input value={form.remark} onChange={(e) => setForm(f => ({ ...f, remark: e.target.value }))} data-testid="entry-grade-note" className="mt-1" />
            </div>
          </div>
        )}

        <button type="submit" disabled={saving} data-testid="entry-submit" className="vk-btn-primary text-sm">
          <Plus size={14} /> {saving ? "Saving…" : "Add entry"}
        </button>
      </form>

      {/* Progress entries list */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-[#0B192C]">Progress history ({progress.length})</h2>
        </div>
        {progress.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No entries yet. Add the first one above.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {progress.map(e => (
              <li key={e.id} className="p-4 flex items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-[#FF6B35]">{e.entry_type}</span>
                    <span className="text-xs text-slate-400">· {e.entry_date || new Date(e.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="mt-1 text-sm text-[#0B192C]">
                    {e.entry_type === "test" && (
                      <span>{e.subject}{e.chapter ? ` · ${e.chapter}` : ""}{e.test_name ? ` · ${e.test_name}` : ""} — <b>{e.marks_obtained}/{e.max_marks}</b> ({e.percentage}%)</span>
                    )}
                    {e.entry_type === "attendance" && (<span>Attendance <b>{e.attendance_percent}%</b>{e.remark ? ` · ${e.remark}` : ""}</span>)}
                    {e.entry_type === "remark" && (<span className="italic">&ldquo;{e.remark}&rdquo;</span>)}
                    {e.entry_type === "grade" && (<span>{e.subject}: Grade <b>{e.grade_value}</b>{e.remark ? ` · ${e.remark}` : ""}</span>)}
                  </div>
                </div>
                <button onClick={() => removeEntry(e.id)} className="text-slate-400 hover:text-red-500" title="Delete" data-testid={`del-entry-${e.id}`}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
